'use client';

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { useStudentPartner } from './StudentPartnerContext';
import { fetchApi } from '@/lib/api/client';
import type { EventRegistration } from '@/types';

// A registered event counts as "live now" if it's actively running, or if it
// starts within this window (covers the gap before the backend flips an
// event's status to LIVE) and hasn't ended yet. Mirrors the LIVE/PAUSED
// status check used by the home page's "Live Events" widget (app/page.tsx).
const IMMINENT_WINDOW_MS = 15 * 60 * 1000;
const RECHECK_INTERVAL_MS = 60 * 1000;

function isLiveOrImminent(event: EventRegistration['event']): boolean {
  if (event.status === 'LIVE' || event.status === 'PAUSED') return true;
  if (event.status === 'COMPLETED' || event.status === 'CANCELLED') return false;

  const now = Date.now();
  const startsAt = new Date(event.startAt).getTime();
  const endsAt = new Date(event.endAt).getTime();
  return startsAt - now <= IMMINENT_WINDOW_MS && endsAt > now;
}

// Stricter than isLiveOrImminent above (no early "imminent" window) — this is
// what decides the actual LIVE vs UPCOMING banner state on the dashboard home
// (LiveEventAttentionBanner), not just a heads-up badge. Falls back to the
// startAt/endAt window when status hasn't flipped to LIVE/PAUSED yet, since
// the backend applies that transition with some lag.
export function isEventLiveNow(event: EventRegistration['event'], nowMs: number = Date.now()): boolean {
  if (event.status === 'COMPLETED' || event.status === 'CANCELLED') return false;
  if (event.status === 'LIVE' || event.status === 'PAUSED') return true;
  const startsAt = new Date(event.startAt).getTime();
  const endsAt = new Date(event.endAt).getTime();
  return nowMs >= startsAt && nowMs < endsAt;
}

function isEventUpcoming(event: EventRegistration['event'], nowMs: number = Date.now()): boolean {
  if (isEventLiveNow(event, nowMs)) return false;
  if (event.status === 'COMPLETED' || event.status === 'CANCELLED') return false;
  return new Date(event.startAt).getTime() > nowMs;
}

// Picks the single most-relevant registration for the "needs attention"
// banner: a LIVE event beats any UPCOMING one, and ties break on the nearest
// startAt. Ignores registrations the user cancelled or no-showed, and events
// that are DRAFT/COMPLETED/CANCELLED/CALCULATING.
function pickAttentionRegistration(registrations: EventRegistration[]): EventRegistration | null {
  const eligible = registrations.filter((reg) => reg.status === 'REGISTERED');
  const byStartAtAsc = (a: EventRegistration, b: EventRegistration) =>
    new Date(a.event.startAt).getTime() - new Date(b.event.startAt).getTime();

  const live = eligible.filter((reg) => isEventLiveNow(reg.event)).sort(byStartAtAsc);
  if (live.length > 0) return live[0];

  const upcoming = eligible.filter((reg) => isEventUpcoming(reg.event)).sort(byStartAtAsc);
  return upcoming[0] ?? null;
}

interface LiveEventState {
  hasLiveNow: boolean;
  /** Most-relevant registered event for the dashboard home's attention banner. */
  attentionRegistration: EventRegistration | null;
}

const LiveEventContext = createContext<LiveEventState>({
  hasLiveNow: false,
  attentionRegistration: null,
});

export function LiveEventProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const { partner } = useStudentPartner();
  const [registrations, setRegistrations] = useState<EventRegistration[]>([]);
  const [hasLiveNow, setHasLiveNow] = useState(false);
  const [attentionRegistration, setAttentionRegistration] = useState<EventRegistration | null>(null);

  // Fetch once per session — reuses the same `/live-events/my-events`
  // endpoint as the dashboard home page's "Live Events" widget (app/page.tsx).
  // This provider is mounted once in the root layout, which persists across
  // client-side navigation, so this effect does not re-run per page/sidebar
  // render. Skipped entirely for bimbel-scoped (partner) students, since
  // their sidebar never shows the Events item (see Layout.tsx visibleNav).
  useEffect(() => {
    if (!isAuthenticated || partner) {
      return;
    }

    let cancelled = false;
    fetchApi<EventRegistration[]>('/live-events/my-events')
      .then((data) => {
        if (!cancelled) setRegistrations(data);
      })
      .catch(() => {
        // Fail quiet — no badge, no error UI, this is decorative only.
        if (!cancelled) setRegistrations([]);
      });

    // Clears stale registrations when auth/partner status changes (e.g. logout)
    // or on unmount — runs on the *next* dependency change, not synchronously
    // within this effect's own body.
    return () => {
      cancelled = true;
      setRegistrations([]);
    };
  }, [isAuthenticated, partner]);

  // Re-derive the badge boolean (and the attention-banner pick) from the
  // already-fetched data on a light interval. No network calls here — just
  // re-evaluates the time windows so both can flip as events start and end
  // without re-fetching. The banner itself ticks every second internally for
  // its live countdown text; this interval only needs to catch coarser
  // changes like "which registered event is now the most relevant one."
  useEffect(() => {
    const recompute = () => {
      setHasLiveNow(registrations.some((reg) => isLiveOrImminent(reg.event)));
      setAttentionRegistration(pickAttentionRegistration(registrations));
    };
    recompute();
    const timer = setInterval(recompute, RECHECK_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [registrations]);

  return (
    <LiveEventContext.Provider value={{ hasLiveNow, attentionRegistration }}>
      {children}
    </LiveEventContext.Provider>
  );
}

export function useLiveEvent(): LiveEventState {
  return useContext(LiveEventContext);
}
